import { useMemo } from 'react';

import type { TFunction } from 'i18next';
import {
  EAssetTypeVariant,
  EAttachmentType,
  type IChatMessage,
  type IconType,
  type TAssetTypeVariant,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';

import { useRetrieveWorkspaceBookConnection } from '@/generated/hooks/bdk/v2/services/library';
import {
  useGetCollection,
  useGetCollectionSchema,
  useGetFolder,
} from '@/generated/hooks/bishop/collections/v1/api';
import type { V1ValueScalarType } from '@/generated/hooks/bishop/models';
import { FIELD_TYPE_ICON, FIELD_TYPE_LABEL_KEY } from '@/modules/library/utils';
import { ROUTES } from '@/routes/util';
import { parseResourceName } from '@/shared/utils';
import {
  detectAttachmentType,
  extractFilenameFromResource,
} from '@/shared/utils/chat-utils';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

import {
  RainApprovalCard,
  type IRainApprovalField,
  type IRainApprovalChange,
  type IRainApprovalRequest,
} from '../cards/RainApprovalCard';
import type {
  IRainToolCallState,
  IRainToolRenderContext,
  IRainToolResolvers,
} from '../../hooks/types';
import {
  asTrimmed,
  diffNames,
  diffSchemaFields,
  parseNames,
  parseProposedFields,
  readCurrentFields,
  type IQuillNameChange,
  type IQuillSchemaField,
  type IQuillSchemaFieldChange,
} from '../quill-schema';
import { extractCreatedCollectionId } from '../quill-run-result';

/**
 * The decision wiring every approval card shares. Answering is only offered
 * while the loop is actually paused on this call: before the approval request
 * arrives there is nothing to answer.
 */
function QuillApprovalCard({
  state,
  resolvers,
  request,
}: {
  state: IRainToolCallState;
  resolvers: IRainToolResolvers;
  request: IRainApprovalRequest;
}) {
  const canDecide = state.awaitingApproval === true;

  return (
    <RainApprovalCard
      request={request}
      decision={state.approvalDecision}
      status={state.status}
      denialReason={state.denialReason}
      errorMessage={state.errorMessage}
      isSubmitting={state.isSubmitting}
      onApprove={
        canDecide
          ? () => resolvers.submitToolApproval(state.id, true)
          : undefined
      }
      onDeny={
        canDecide
          ? () => resolvers.submitToolApproval(state.id, false)
          : undefined
      }
    />
  );
}

/** Joins the parts of a change summary or a field's detail line. */
const SEPARATOR = ' · ';

const countKind = (
  changes: readonly { kind: string }[],
  kind: string,
): number => changes.filter(change => change.kind === kind).length;

/**
 * "1 removed · 2 added", listing only what actually changes, or a stand-in when
 * nothing does.
 */
const joinCounts = (
  counts: readonly { count: number; key: string }[],
  noneKey: string,
  t: TFunction,
): string => {
  const said = counts
    .filter(part => part.count > 0)
    .map(part => t(part.key, { count: part.count }));
  return said.length > 0 ? said.join(SEPARATOR) : t(noneKey);
};

/**
 * Whether the call ended without its change being made. What the tool was asked
 * to do must then be shown as a proposal: refused or failed, it describes
 * nothing that exists.
 *
 * Refused or errored only. Granting leaves the call `running` until its result
 * lands, and a change on its way is not one that never happened — reading this
 * as "not applied" made the asset card offer a "Proposed file" it was already
 * saving.
 */
const isUnapplied = (state: IRainToolCallState): boolean =>
  state.approvalDecision === 'denied' || state.status === 'error';

// A field type reads the same here as in the Library's own schema tab, so both
// the label and the icon come from the Library's vocabulary rather than a second
// copy of it. The label lives in that module's namespace, hence the `ns`.
const typeLabel = (
  scalarType: V1ValueScalarType | undefined,
  t: TFunction,
): string | undefined =>
  scalarType === undefined
    ? undefined
    : t(FIELD_TYPE_LABEL_KEY[scalarType], { ns: 'library' });

/** Not every scalar has an icon of its own; the rest read as plain values. */
const typeIcon = (scalarType: V1ValueScalarType | undefined): IconType =>
  (scalarType && FIELD_TYPE_ICON[scalarType]) ?? 'Type';

/** The proposed fields as rows, in the Library's own type vocabulary. */
const asFields = (
  fields: IQuillSchemaField[],
  t: TFunction,
): IRainApprovalField[] =>
  fields.map((field, index) => ({
    id: `${index}-${field.name}`,
    name: field.name,
    typeIcon: typeIcon(field.scalarType),
    typeLabel: typeLabel(field.scalarType, t) ?? '',
    required: field.required,
  }));

/**
 * What is different about a field, in one line. An addition or a removal has
 * only its type to say; an edit says what it was and what it becomes, since
 * that is the part an approver has to weigh.
 */
const changeDetail = (
  change: IQuillSchemaFieldChange,
  t: TFunction,
): string | undefined => {
  const label = typeLabel(change.field.scalarType, t);

  if (change.kind === 'changed') {
    return change.reasons
      .map(reason => {
        if (reason === 'type') {
          return t('automation.quill2.approval.schema.change.type', {
            from: typeLabel(change.previousScalarType, t),
            to: label,
          });
        }
        if (reason === 'required') {
          return t(
            change.field.required
              ? 'automation.quill2.approval.schema.change.nowRequired'
              : 'automation.quill2.approval.schema.change.nowOptional',
          );
        }
        return t(
          change.field.unique
            ? 'automation.quill2.approval.schema.change.nowUnique'
            : 'automation.quill2.approval.schema.change.nowNotUnique',
        );
      })
      .join(SEPARATOR);
  }

  if (change.kind === 'added' && change.field.required) {
    return [label, t('automation.quill2.approval.field.required')]
      .filter(Boolean)
      .join(SEPARATOR);
  }
  return label;
};

const SCHEMA_SUMMARY_KEY = {
  removed: 'automation.quill2.approval.schema.summary.removed',
  changed: 'automation.quill2.approval.schema.summary.changed',
  added: 'automation.quill2.approval.schema.summary.added',
} as const;

/** How much changes, most consequential first, so the header leads with it. */
const changeSummary = (
  changes: IQuillSchemaFieldChange[],
  t: TFunction,
): string =>
  joinCounts(
    (['removed', 'changed', 'added'] as const).map(kind => ({
      count: countKind(changes, kind),
      key: SCHEMA_SUMMARY_KEY[kind],
    })),
    'automation.quill2.approval.schema.summary.none',
    t,
  );

const FIELD_BADGE_KEY = {
  removed: 'automation.quill2.approval.change.removed',
  changed: 'automation.quill2.approval.change.changed',
  added: 'automation.quill2.approval.change.added',
} as const;

/** The changed fields as rows. What stays the same is left to a count. */
const asChangeRows = (
  changes: IQuillSchemaFieldChange[],
  t: TFunction,
): IRainApprovalChange[] =>
  changes.flatMap((change, index) =>
    change.kind === 'unchanged'
      ? []
      : [
          {
            id: `${index}-${change.field.name}`,
            name: change.field.name,
            badge: {
              kind: change.kind,
              label: t(FIELD_BADGE_KEY[change.kind]),
            },
            icon: typeIcon(change.field.scalarType),
            detail: changeDetail(change, t),
          },
        ],
  );

/**
 * The attachment type the chat already infers from a filename, as the DS asset
 * variant for it. Neither the real mime type nor the size is known until the
 * asset exists, so the extension is all there is to go on.
 *
 * Read at call time rather than as a module-level map: a keyed object would
 * evaluate these enums while the module loads, which throws in any test that
 * partially mocks the DS without them, however unrelated that test is.
 */
const asAssetType = (filename: string): TAssetTypeVariant => {
  switch (detectAttachmentType(filename)) {
    case EAttachmentType.PDF:
      return EAssetTypeVariant.PDF;
    case EAttachmentType.EXCEL:
      return EAssetTypeVariant.Spreadsheet;
    case EAttachmentType.IMAGE:
      return EAssetTypeVariant.Image;
    case EAttachmentType.TEXT:
      return EAssetTypeVariant.Document;
    default:
      return EAssetTypeVariant.Unknown;
  }
};

/**
 * `create_collection` — the whole schema is new, so the card lists it as
 * proposed and links at the collection once it exists.
 */
function QuillCreateCollectionCard({
  state,
  resolvers,
}: {
  state: IRainToolCallState;
  resolvers: IRainToolResolvers;
}) {
  const { t } = useTranslation('automation');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const fields = useMemo(
    () => asFields(parseProposedFields(state.args.fields), t),
    [state.args.fields, t],
  );
  const collectionName =
    asTrimmed(state.args.displayName) ??
    t('automation.quill2.approval.collection.fallbackName');

  const resourceHref = useMemo(() => {
    const collectionId = extractCreatedCollectionId(state.result);
    if (!collectionId || !organizationId || !workspaceId) return undefined;
    return ROUTES.LIBRARY_COLLECTION.build({
      orgId: organizationId,
      workspaceId,
      collectionId,
    });
  }, [organizationId, workspaceId, state.result]);

  const request: IRainApprovalRequest = {
    pendingTitle: t('automation.quill2.approval.collection.pendingTitle', {
      name: collectionName,
    }),
    pendingDescription: t(
      'automation.quill2.approval.collection.pendingDescription',
    ),
    runningSummary: t('automation.quill2.approval.collection.runningSummary', {
      name: collectionName,
    }),
    approvedSummary: t(
      'automation.quill2.approval.collection.approvedSummary',
      { name: collectionName },
    ),
    failedSummary: t('automation.quill2.approval.collection.failedSummary', {
      name: collectionName,
    }),
    failedTitle: t('automation.quill2.approval.collection.failedTitle'),
    failedDescription: t(
      'automation.quill2.approval.collection.failedDescription',
    ),
    deniedSummary: t('automation.quill2.approval.collection.deniedSummary', {
      name: collectionName,
    }),
    approveLabel: t('automation.quill2.approval.collection.approve'),
    denyLabel: t('automation.quill2.approval.collection.deny'),
    resourceLinkLabel: t('automation.quill2.approval.collection.view'),
    resourceHref,
    content: {
      kind: 'collection',
      collectionName,
      fieldCountLabel: t('automation.quill2.approval.collection.fieldCount', {
        count: fields.length,
      }),
      fields,
      proposalHeading: isUnapplied(state)
        ? t('automation.quill2.approval.collection.proposedHeading')
        : undefined,
    },
  };

  return (
    <QuillApprovalCard state={state} resolvers={resolvers} request={request} />
  );
}

/**
 * `update_collection_schema` — the tool sends the whole field list, so what it
 * actually changes is only visible against the schema the collection has now.
 * Approving can drop a field and its stored values without the backend
 * objecting, so while the decision is open the card diffs the two and says what
 * that costs.
 *
 * Once answered it shows the field list plainly instead: the diff was a claim
 * about a call that had not happened yet, and re-running it against the schema
 * the call itself produced reports no changes at all.
 */
function QuillUpdateCollectionSchemaCard({
  state,
  resolvers,
}: {
  state: IRainToolCallState;
  resolvers: IRainToolResolvers;
}) {
  const { t } = useTranslation('automation');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const collectionId = parseResourceName(asTrimmed(state.args.name)).collection;
  const canQuery = !!collectionId && !!organizationId && !!workspaceId;
  // A diff is a claim about what a call *would* do, so it only holds while the
  // call has not been made. Once answered, the collection's live schema is no
  // longer the "before" of anything — it is the after, or something a later
  // edit has moved on again — so the card stops asking for it.
  const isPending = state.approvalDecision === undefined;
  // No stage: the tool always writes the draft, which is what both queries
  // return by default, so the diff and the row count are the draft's own.
  const { data: schema, error: schemaError } = useGetCollectionSchema(
    organizationId,
    workspaceId,
    collectionId ?? '',
    undefined,
    // A collection with no schema yet answers 404, which is an answer rather
    // than a fault, so retrying only repeats it.
    { query: { enabled: canQuery && isPending, retry: false } },
  );
  const { data: collection } = useGetCollection(
    organizationId,
    workspaceId,
    collectionId ?? '',
    undefined,
    { query: { enabled: canQuery } },
  );

  const proposed = useMemo(
    () => parseProposedFields(state.args.fields),
    [state.args.fields],
  );
  const currentFields = schema?.data.fields;
  // A 404 is bishop saying the collection has no schema yet, which is the same
  // as having no fields — every proposed field really is new. Any other failure
  // leaves the current schema unknown, and then there is nothing to diff
  // against: a field must not be called new, or a removal missed, on a guess.
  const hasNoSchema = schemaError?.response?.status === 404;
  const changes = useMemo(
    () =>
      isPending && (schema || hasNoSchema)
        ? diffSchemaFields(proposed, readCurrentFields(currentFields))
        : undefined,
    [isPending, proposed, schema, hasNoSchema, currentFields],
  );

  const collectionName =
    asTrimmed(collection?.data.display_name) ??
    t('automation.quill2.approval.schema.fallbackName');

  const removedCount = changes ? countKind(changes, 'removed') : 0;
  const unchangedCount = changes ? countKind(changes, 'unchanged') : 0;
  // Approximate and derived from partition bookkeeping, so it is only used to
  // decide whether there is data at stake at all.
  const recordCount = Number(collection?.data.record_count ?? 0);

  const content: IRainApprovalRequest['content'] = changes
    ? {
        kind: 'changes',
        name: collectionName,
        icon: 'Database',
        changeSummary: changeSummary(changes, t),
        changes: asChangeRows(changes, t),
        unchangedLabel:
          unchangedCount > 0
            ? t('automation.quill2.approval.schema.unchanged', {
                count: unchangedCount,
              })
            : undefined,
      }
    : {
        kind: 'collection',
        collectionName,
        fieldCountLabel: t('automation.quill2.approval.collection.fieldCount', {
          count: proposed.length,
        }),
        fields: asFields(proposed, t),
        proposalHeading: isUnapplied(state)
          ? t('automation.quill2.approval.schema.proposedHeading')
          : undefined,
      };

  const request: IRainApprovalRequest = {
    pendingTitle: t('automation.quill2.approval.schema.pendingTitle', {
      name: collectionName,
    }),
    pendingDescription: t(
      'automation.quill2.approval.schema.pendingDescription',
    ),
    runningSummary: t('automation.quill2.approval.schema.runningSummary', {
      name: collectionName,
    }),
    approvedSummary: t('automation.quill2.approval.schema.approvedSummary', {
      name: collectionName,
    }),
    failedSummary: t('automation.quill2.approval.schema.failedSummary', {
      name: collectionName,
    }),
    failedTitle: t('automation.quill2.approval.schema.failedTitle'),
    failedDescription: t('automation.quill2.approval.schema.failedDescription'),
    deniedSummary: t('automation.quill2.approval.schema.deniedSummary', {
      name: collectionName,
    }),
    approveLabel: t('automation.quill2.approval.schema.approve'),
    denyLabel: t('automation.quill2.approval.schema.deny'),
    warning:
      removedCount > 0
        ? {
            title: t('automation.quill2.approval.schema.warning.title', {
              count: removedCount,
            }),
            description: [
              t('automation.quill2.approval.schema.warning.description'),
              recordCount > 0
                ? t('automation.quill2.approval.schema.warning.records', {
                    count: recordCount,
                    name: collectionName,
                  })
                : undefined,
            ]
              .filter(Boolean)
              .join(' '),
          }
        : undefined,
    isDestructive: removedCount > 0,
    resourceLinkLabel: t('automation.quill2.approval.schema.view'),
    resourceHref:
      collectionId && organizationId && workspaceId
        ? ROUTES.LIBRARY_COLLECTION.build({
            orgId: organizationId,
            workspaceId,
            collectionId,
          })
        : undefined,
    content,
  };

  return (
    <QuillApprovalCard state={state} resolvers={resolvers} request={request} />
  );
}

const DISCOVERY_SUMMARY_KEY = {
  removed: 'automation.quill2.approval.discovery.summary.disabling',
  added: 'automation.quill2.approval.discovery.summary.enabling',
} as const;

/**
 * `set_discovered` — the entity names, verbatim: the Integrations page already
 * lists them that way in its own Entities column, so a raw SAP or Salesforce
 * name is not a leak.
 *
 * Only what is being switched off is badged. Every row here is a change and the
 * unchanged entities are already a count, so the safe direction needs no label,
 * which leaves the one that breaks automations as the only badge on the card.
 */
const asDiscoveryRows = (
  changes: IQuillNameChange[],
  t: TFunction,
): IRainApprovalChange[] =>
  changes.flatMap((change, index) =>
    change.kind === 'unchanged'
      ? []
      : [
          {
            id: `${index}-${change.name}`,
            name: change.name,
            badge:
              change.kind === 'removed'
                ? {
                    kind: 'removed',
                    label: t(
                      'automation.quill2.approval.discovery.badge.disabling',
                    ),
                  }
                : undefined,
          },
        ],
  );

const discoverySummary = (changes: IQuillNameChange[], t: TFunction): string =>
  joinCounts(
    (['removed', 'added'] as const).map(kind => ({
      count: countKind(changes, kind),
      key: DISCOVERY_SUMMARY_KEY[kind],
    })),
    'automation.quill2.approval.discovery.summary.none',
    t,
  );

/** The enabled set as a plain list, for a card that is no longer deciding. */
const asDiscoveryList = (names: string[]): IRainApprovalChange[] =>
  names.map((name, index) => ({ id: `${index}-${name}`, name }));

/**
 * `set_discovered` — a PUT, like `update_collection_schema`: `what` carries the
 * complete set of entities to leave switched on, so one it omits is switched
 * off and every automation calling that entity's actions breaks. While the
 * decision is open the card diffs it against the connection's current
 * `discovered` list and says which actions go away.
 *
 * Once answered it lists `what` plainly, for the same reason the schema card
 * does: the argument *is* the resulting set, and re-diffing it against a
 * connection that now matches would report no changes at all.
 */
function QuillSetDiscoveredCard({
  state,
  resolvers,
}: {
  state: IRainToolCallState;
  resolvers: IRainToolResolvers;
}) {
  const { t } = useTranslation('automation');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const connectionId = asTrimmed(state.args.connectionId) ?? '';
  const isPending = state.approvalDecision === undefined;
  const { data: connectionData } = useRetrieveWorkspaceBookConnection(
    organizationId,
    workspaceId,
    connectionId,
    {
      query: {
        enabled: !!connectionId && !!organizationId && !!workspaceId,
      },
    },
  );
  const connection = connectionData?.data.book_connection;

  const proposed = useMemo(
    () => parseNames(state.args.what),
    [state.args.what],
  );
  // bdk reports only what is switched on, so this is the whole of the "before".
  // Absent means nothing has been discovered yet, which is a first run rather
  // than an unknown: every proposed entity really is being switched on.
  const current = connection?.discovered;
  const changes = useMemo(
    () =>
      isPending && connection ? diffNames(proposed, current ?? []) : undefined,
    [isPending, connection, proposed, current],
  );

  const connectionName =
    asTrimmed(connection?.connection_name) ??
    asTrimmed(connection?.book_name) ??
    t('automation.quill2.approval.discovery.fallbackName');

  const removedCount = changes ? countKind(changes, 'removed') : 0;
  const unchangedCount = changes ? countKind(changes, 'unchanged') : 0;

  const content: IRainApprovalRequest['content'] = {
    kind: 'changes',
    name: connectionName,
    icon: 'Blocks',
    changeSummary: changes
      ? discoverySummary(changes, t)
      : t('automation.quill2.approval.discovery.actionCount', {
          count: proposed.length,
        }),
    changes: changes ? asDiscoveryRows(changes, t) : asDiscoveryList(proposed),
    unchangedLabel:
      unchangedCount > 0
        ? t('automation.quill2.approval.discovery.unchanged', {
            count: unchangedCount,
          })
        : undefined,
    proposalHeading: isUnapplied(state)
      ? t('automation.quill2.approval.discovery.proposedHeading')
      : undefined,
  };

  const request: IRainApprovalRequest = {
    pendingTitle: t('automation.quill2.approval.discovery.pendingTitle', {
      name: connectionName,
    }),
    pendingDescription: t(
      'automation.quill2.approval.discovery.pendingDescription',
    ),
    runningSummary: t('automation.quill2.approval.discovery.runningSummary', {
      name: connectionName,
    }),
    approvedSummary: t('automation.quill2.approval.discovery.approvedSummary', {
      name: connectionName,
    }),
    failedSummary: t('automation.quill2.approval.discovery.failedSummary', {
      name: connectionName,
    }),
    failedTitle: t('automation.quill2.approval.discovery.failedTitle'),
    failedDescription: t(
      'automation.quill2.approval.discovery.failedDescription',
    ),
    deniedSummary: t('automation.quill2.approval.discovery.deniedSummary', {
      name: connectionName,
    }),
    approveLabel: t('automation.quill2.approval.discovery.approve'),
    denyLabel: t('automation.quill2.approval.discovery.deny'),
    // Nothing is deleted, so the warning is about automations breaking rather
    // than about data loss.
    warning:
      removedCount > 0
        ? {
            title: t('automation.quill2.approval.discovery.warning.title', {
              count: removedCount,
            }),
            description: t(
              'automation.quill2.approval.discovery.warning.description',
            ),
          }
        : undefined,
    isDestructive: removedCount > 0,
    resourceLinkLabel: t('automation.quill2.approval.discovery.view'),
    // No route reaches a single connection, so the link opens the list the
    // "Configure actions" menu lives on.
    resourceHref:
      organizationId && workspaceId
        ? ROUTES.INTEGRATIONS_CONNECTIONS.build({
            orgId: organizationId,
            workspaceId,
          })
        : undefined,
    content,
  };

  return (
    <QuillApprovalCard state={state} resolvers={resolvers} request={request} />
  );
}

/**
 * `create_asset` — registers a file that already exists as a workspace asset.
 * There is no asset-detail route, so the link opens the folder it landed in.
 */
function QuillCreateAssetCard({
  state,
  resolvers,
}: {
  state: IRainToolCallState;
  resolvers: IRainToolResolvers;
}) {
  const { t } = useTranslation('automation');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const folderName = asTrimmed(state.args.folder);
  const folderId = folderName?.split('/').pop();
  // The argument is a resource name, so the folder's own name has to be looked
  // up; until it resolves the card says where it lands without naming it.
  const { data: folder } = useGetFolder(
    organizationId,
    workspaceId,
    folderId ?? '',
    { query: { enabled: !!folderId && !!organizationId && !!workspaceId } },
  );

  const assetName =
    asTrimmed(state.args.displayName) ??
    t('automation.quill2.approval.asset.fallbackName');
  // `fileUri` is an artifact URI (`scratch://`, `uploads://`), which the
  // resource-path helper does not know about — the scheme is not a filename.
  const sourceName = extractFilenameFromResource(
    (asTrimmed(state.args.fileUri) ?? '').replace(/^\w+:\/\//, ''),
  );

  const destination =
    asTrimmed(folder?.data.display_name) ??
    (folderId
      ? t('automation.quill2.approval.asset.someFolder')
      : t('automation.quill2.approval.asset.workspaceRoot'));

  const resourceHref =
    organizationId && workspaceId
      ? folderId
        ? ROUTES.LIBRARY_FOLDER.build({
            orgId: organizationId,
            workspaceId,
            folderId,
          })
        : ROUTES.LIBRARY.build({ orgId: organizationId, workspaceId })
      : undefined;

  const unapplied = isUnapplied(state);

  const request: IRainApprovalRequest = {
    pendingTitle: t('automation.quill2.approval.asset.pendingTitle', {
      name: assetName,
    }),
    pendingDescription: t(
      'automation.quill2.approval.asset.pendingDescription',
    ),
    runningSummary: t('automation.quill2.approval.asset.runningSummary', {
      name: assetName,
    }),
    approvedSummary: t('automation.quill2.approval.asset.approvedSummary', {
      name: assetName,
    }),
    failedSummary: t('automation.quill2.approval.asset.failedSummary', {
      name: assetName,
    }),
    failedTitle: t('automation.quill2.approval.asset.failedTitle'),
    failedDescription: t('automation.quill2.approval.asset.failedDescription'),
    deniedSummary: t('automation.quill2.approval.asset.deniedSummary', {
      name: assetName,
    }),
    approveLabel: t('automation.quill2.approval.asset.approve'),
    denyLabel: t('automation.quill2.approval.asset.deny'),
    resourceLinkLabel: t('automation.quill2.approval.asset.view'),
    resourceHref,
    content: {
      kind: 'asset',
      name: assetName,
      metadata: sourceName,
      assetType: asAssetType(sourceName),
      destinationLabel: t(
        unapplied
          ? 'automation.quill2.approval.asset.wouldSaveTo'
          : 'automation.quill2.approval.asset.savesTo',
      ),
      destination,
      proposalHeading: unapplied
        ? t('automation.quill2.approval.asset.proposedHeading')
        : undefined,
    },
  };

  return (
    <QuillApprovalCard state={state} resolvers={resolvers} request={request} />
  );
}

/** `create_collection` renders the approval card rather than the generic one. */
export function renderCreateCollectionCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  return ctx.renderCustomCard(
    state,
    <QuillCreateCollectionCard state={state} resolvers={ctx.resolvers} />,
  );
}

/** `update_collection_schema` renders the approval card, with a diff in it. */
export function renderUpdateCollectionSchemaCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  return ctx.renderCustomCard(
    state,
    <QuillUpdateCollectionSchemaCard state={state} resolvers={ctx.resolvers} />,
  );
}

/** `set_discovered` renders the approval card, with a diff of the entities. */
export function renderSetDiscoveredCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  return ctx.renderCustomCard(
    state,
    <QuillSetDiscoveredCard state={state} resolvers={ctx.resolvers} />,
  );
}

/** `create_asset` renders the approval card rather than the generic one. */
export function renderCreateAssetCard(
  state: IRainToolCallState,
  ctx: IRainToolRenderContext,
): IChatMessage {
  return ctx.renderCustomCard(
    state,
    <QuillCreateAssetCard state={state} resolvers={ctx.resolvers} />,
  );
}
